import { DataPointArray, isDefined } from '@evorait/pace-core';
import { on } from '@ngrx/store';
import { Choices } from '../../../types/choice';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../../types/functional-element';
import { Signature } from '../../../types/signature';
import { DEFICIENCY_SELECTION, MEASUREMENT_SELECTION, SystemCondition } from '../../../types/system-condition';
import {
    updateSystemConditionSignature,
    updateSystemConditionSignatureDisabled,
    updateSystemConditionSignatureName,
} from '../../actions/functional-elements.actions';
import {
    updateSystemConditionDeficienciesSelection,
    updateSystemConditionDeficiency,
    updateSystemConditionMeasurementsSelection,
    updateSystemConditionRemarks,
} from '../../actions/system-condition.actions';
import { FormState } from '../../state/form.state';
import { invalidateSignatures } from './form-reducer.utilities';

export function updateSystemConditionMeasurementSelectionReducer(
    state: Readonly<FormState>,
    props: { selected: MEASUREMENT_SELECTION; xmlId: string },
): Readonly<FormState> {
    const oldSystemCondition = state.systemCondition;
    if (typeof oldSystemCondition === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionMeasurementSelectionReducer: Trying to update non-existing system condition!`,
        );
    }

    if (oldSystemCondition.measurementSelection === props.selected) {
        return state;
    }

    const newSystemCondition: SystemCondition = {
        ...oldSystemCondition,
        measurementSelection: props.selected,
    };
    const newForm: FormState = {
        ...state,
        systemCondition: newSystemCondition,
    };

    return invalidateSignatures(newForm);
}

export function updateSystemConditionDeficienciesSelectionReducer(
    state: Readonly<FormState>,
    props: { selected: DEFICIENCY_SELECTION; xmlId: string },
): Readonly<FormState> {
    const oldSystemCondition = state.systemCondition;
    if (typeof oldSystemCondition === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionDeficienciesSelectionReducer: Trying to update non-existing system condition!`,
        );
    }

    if (oldSystemCondition.deficienciesSelection === props.selected) {
        return state;
    }

    const newSystemCondition: SystemCondition = {
        ...oldSystemCondition,
        deficienciesSelection: props.selected,
    };
    const newForm: FormState = {
        ...state,
        systemCondition: newSystemCondition,
    };

    return invalidateSignatures(newForm);
}

export function updateSystemConditionRemarksReducer(
    state: Readonly<FormState>,
    props: { newValue: string; xmlId: string },
): Readonly<FormState> {
    const oldSystemCondition = state.systemCondition;
    if (typeof oldSystemCondition === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionRemarksReducer: Trying to update non-existing system condition!`,
        );
    }

    if (oldSystemCondition.remarksValue === props.newValue) {
        return state;
    }

    const newSystemCondition: SystemCondition = {
        ...oldSystemCondition,
        remarksValue: props.newValue,
    };
    const newForm: FormState = {
        ...state,
        systemCondition: newSystemCondition,
    };

    return invalidateSignatures(newForm);
}

export function updateSystemConditionSignatureReducer(
    state: Readonly<FormState>,
    props: {
        signatureType: Signature['type'];
        newSignature: string;
        date: string;
        dateXmlId: string;
        signatureDataPoints: DataPointArray;
    },
): Readonly<FormState> {
    const systemCondition = state.systemCondition;
    if (typeof systemCondition === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionSignatureReducer: Trying to update signature in non-existing system condition!`,
        );
    }

    const isEmployeeSignature =
        props.signatureType === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION ||
        props.signatureType === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE;

    const oldSignature = isEmployeeSignature
        ? state.systemCondition?.employeeSignature
        : state.systemCondition?.customerSignature;

    if (typeof oldSignature === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionSignatureReducer: Trying to update non-existing signature in system condition!`,
        );
    }

    const newSignature: Signature = {
        ...oldSignature,
        signature: props.newSignature,
        date: props.date,
        signatureDataPoints: [...props.signatureDataPoints],
    };

    const newSystemCondition: SystemCondition = {
        ...systemCondition,
        [isEmployeeSignature ? 'employeeSignature' : 'customerSignature']: newSignature,
    };

    return {
        ...state,
        systemCondition: newSystemCondition,
    };
}

export function updateSystemConditionSignatureNameReducer(
    state: Readonly<FormState>,
    props: {
        signatureType: Signature['type'];
        xmlId: string;
        firstName: string;
        lastName: string;
    },
): Readonly<FormState> {
    if (typeof state.systemCondition === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionSignatureNameReducer: Trying to update signature in non-existing system condition!`,
        );
    }

    const isEmployeeSignature =
        props.signatureType === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION ||
        props.signatureType === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE;

    const oldSignature = isEmployeeSignature
        ? state.systemCondition?.employeeSignature
        : state.systemCondition?.customerSignature;

    if (typeof oldSignature === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionSignatureNameReducer: Trying to update non-existing signature in system condition!`,
        );
    }

    const newSignature: Signature = {
        ...oldSignature,
        firstName: props.firstName,
        lastName: props.lastName,
    };

    const newSystemCondition: SystemCondition = {
        ...state.systemCondition,
        [isEmployeeSignature ? 'employeeSignature' : 'customerSignature']: newSignature,
    };

    return {
        ...state,
        systemCondition: newSystemCondition,
    };
}

export function updateSystemConditionSignatureDisabledReducer(
    state: Readonly<FormState>,
    props: {
        signatureType: Signature['type'];
        disabled: boolean;
    },
): Readonly<FormState> {
    if (typeof state.systemCondition === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionSignatureDisabledReducer: Trying to update signature in non-existing system condition!`,
        );
    }

    const oldSignature =
        props.signatureType === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION
            ? state.systemCondition?.employeeSignature
            : state.systemCondition?.customerSignature;

    if (typeof oldSignature === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionSignatureDisabledReducer: Trying to update non-existing signature in system condition!`,
        );
    }

    const newSignature: Signature = {
        ...oldSignature,
        disabled: props.disabled,
    };

    const newSystemCondition: SystemCondition = {
        ...state.systemCondition,
        [props.signatureType === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION
            ? 'employeeSignature'
            : 'customerSignature']: newSignature,
    };

    return {
        ...state,
        systemCondition: newSystemCondition,
    };
}

export function updateSystemConditionDeficiencyReducer(
    state: Readonly<FormState>,
    props: {
        xmlId: string | undefined;
    },
): Readonly<FormState> {
    const oldSystemCondition = state.systemCondition;
    if (typeof oldSystemCondition === 'undefined') {
        throw new Error(
            `FormReducer: updateSystemConditionDeficiencyReducer: Trying to update non-existing system condition!`,
        );
    }

    const oldChoices = oldSystemCondition.choices;
    if (!isDefined(oldChoices)) {
        throw new Error(
            `FormReducer: updateSystemConditionDeficiencyReducer: Functional element has no choices attribute!`,
        );
    }

    // Build new choices object
    const newChoices: Choices = Object.keys(oldChoices).reduce((accu, key: keyof Choices) => {
        const choice = oldChoices[key];

        return {
            ...accu,
            [key]: {
                ...choice,
                selected: choice?.xmlId === props.xmlId,
            },
        };
    }, {});

    const newSystemCondition: SystemCondition = {
        ...oldSystemCondition,
        choices: newChoices,
    };
    const newForm: FormState = {
        ...state,
        systemCondition: newSystemCondition,
    };

    return invalidateSignatures(newForm);
}

export const systemConditionReducer = [
    on<FormState, Array<typeof updateSystemConditionMeasurementsSelection>>(
        updateSystemConditionMeasurementsSelection,
        updateSystemConditionMeasurementSelectionReducer,
    ),
    on<FormState, Array<typeof updateSystemConditionDeficienciesSelection>>(
        updateSystemConditionDeficienciesSelection,
        updateSystemConditionDeficienciesSelectionReducer,
    ),
    on<FormState, Array<typeof updateSystemConditionRemarks>>(
        updateSystemConditionRemarks,
        updateSystemConditionRemarksReducer,
    ),
    on<FormState, Array<typeof updateSystemConditionSignature>>(
        updateSystemConditionSignature,
        updateSystemConditionSignatureReducer,
    ),
    on<FormState, Array<typeof updateSystemConditionSignatureName>>(
        updateSystemConditionSignatureName,
        updateSystemConditionSignatureNameReducer,
    ),
    on<FormState, Array<typeof updateSystemConditionSignatureDisabled>>(
        updateSystemConditionSignatureDisabled,
        updateSystemConditionSignatureDisabledReducer,
    ),
    on<FormState, Array<typeof updateSystemConditionDeficiency>>(
        updateSystemConditionDeficiency,
        updateSystemConditionDeficiencyReducer,
    ),
];
